import { davidMorgan, kevinOSullivan, priyaNair } from './archetypes';
import type {
  FindingDraft,
  FindingEvidenceRef,
  FindingProposal,
  FindingsDocument,
} from '../../hooks/useFindingSynthesis';
import type { ActivityOutputReviewItem } from '../../engagement/ActivityOutputReview';
import { buildLumenGridCurrentValueStreamOutput } from './valueStreamMapping';

export type ReviewedFindingEvidence = {
  id: string;
  name: string;
  source: string;
  summary: string;
  relationship: 'supports' | 'challenges';
  ref: FindingEvidenceRef;
  output: ActivityOutputReviewItem;
};

const businessOutput: ActivityOutputReviewItem = Object.freeze({
  id: 'business-domain-model', activityId: 'lumen-grid-discover-business-domains',
  name: 'Business domain model', method: 'Facilitated domain discovery', state: 'reviewed', revision: 1,
  returnedAt: '2026-07-11T12:00:00.000Z', reviewer: davidMorgan,
  contributors: [davidMorgan, kevinOSullivan], sessionIds: ['lumen-grid-domain-session-01'],
  summary: 'Participants distinguish customer, product and vending responsibilities around customer outcomes.',
  assumptions: ['The facilitated model reflects the language used by business participants.'],
  openQuestions: ['Where should shared customer and meter data ownership sit?'],
});

const systemOutput: ActivityOutputReviewItem = Object.freeze({
  id: 'system-domain-model', activityId: 'lumen-grid-technical-estate-analysis',
  name: 'System domain model', method: 'Static code, integration and data analysis', state: 'reviewed', revision: 1,
  returnedAt: '2026-07-14T15:30:00.000Z', reviewer: davidMorgan,
  contributors: [davidMorgan, priyaNair], sessionIds: ['lumen-grid-estate-analysis-01'],
  summary: 'Legacy platform boundaries and shared data cut across customer, product and vending responsibilities.',
  assumptions: ['Repository and integration samples represent the production estate.'],
  openQuestions: ['Is shared data ownership or integration sequencing the primary source of coupling?'],
});

const flowV2 = buildLumenGridCurrentValueStreamOutput({ state: 'reviewed', revision: 2 });
const flowOutput: ActivityOutputReviewItem = Object.freeze({ ...flowV2 });

export const lumenGridReviewedFindingEvidence: ReadonlyArray<ReviewedFindingEvidence> = Object.freeze([
  {
    id: 'business-domain-model-v1', name: businessOutput.name,
    source: 'Map business domains · Version 1', relationship: 'supports',
    summary: businessOutput.summary,
    ref: { activityId: businessOutput.activityId, outputId: businessOutput.id, version: 1 }, output: businessOutput,
  },
  {
    id: 'system-domain-model-v1', name: systemOutput.name,
    source: 'Analyse the technical estate · Version 1', relationship: 'supports',
    summary: systemOutput.summary,
    ref: { activityId: systemOutput.activityId, outputId: systemOutput.id, version: 1 }, output: systemOutput,
  },
  {
    id: 'current-value-stream-model-v2', name: flowOutput.name,
    source: 'Map priority value streams · Version 2', relationship: 'challenges',
    summary: 'Observed operational rekeying limits the claim that software boundaries are the primary cause.',
    ref: { activityId: flowOutput.activityId, outputId: flowOutput.id, version: 2 }, output: flowOutput,
  },
]);

export const businessSystemBoundaryProposal: FindingProposal = Object.freeze({
  title: 'Business/system boundary misalignment', classification: 'problem',
  statement: 'LumenGrid’s customer-onboarding responsibilities are organised around customer outcomes, while the legacy platform and shared data span customer, product and vending concerns.',
  whyItMatters: 'Misaligned ownership makes change coordination slower and obscures where customer outcomes are owned.',
  confidence: 'medium',
  confidenceRationale: 'Two perspectives support the interpretation; the current value stream limits the causal claim.',
  evidence: {
    supports: lumenGridReviewedFindingEvidence.filter((item) => item.relationship === 'supports').map((item) => item.ref),
    challenges: lumenGridReviewedFindingEvidence.filter((item) => item.relationship === 'challenges').map((item) => item.ref),
  },
  uncertainties: [{
    id: 'primary-coupling-source',
    statement: 'We have not established whether shared data ownership or integration sequencing is the primary source of coupling.',
    blocking: false,
  }],
});

export const emptyLumenGridFindings: FindingsDocument = Object.freeze({ version: 1, findings: [] });

export const reconcileDomainPerspectives = Object.freeze({
  id: 'reconcile-domain-perspectives', name: 'Reconcile business and system domain perspectives',
  method: 'Facilitated domain reconciliation',
  purpose: 'Establish whether shared data ownership or integration sequencing is the primary source of coupling.',
});

export function asFindingDraft(proposal: FindingProposal): FindingDraft {
  return { findingId: 'business-system-boundary-misalignment', ...proposal };
}
